// LOOP CRM PROVIDER-AGNOSTIC IDENTITY R35 — picker treats Firebase UID as canonical regardless of password/Google/Apple/OIDC provider.
// LOOP CUSTOMER DOCUMENT ID INVARIANT V20 — picker preserves the real Firestore customer id across search, merge, and selection.
// LOOP CUSTOMER PICKER MOBILE KEYBOARD V13 — keep input/results visible above native keyboard.
// LOOP CUSTOMER PICKER MOBILE LAYERS V94 — compact mobile results and sane dropdown z-index.
// LOOP WORKER BOOKING CUSTOMER IDENTITY V8 — do not clear selected customer on same-name mobile input events.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { collection, getDocs, limit, orderBy, query, where, startAfter } from "firebase/firestore";
import { db } from "../firebase";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "../utils/nativeHttp";
import { normalizeEmail, normalizePhone } from "../utils/customerCrm";
import { formatPhoneDisplay } from "../utils/phoneUtils";

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function formatDate(value) {
  try {
    if (!value) return "";
    if (value.toDate) return value.toDate().toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
    if (typeof value === "string") return value;
    return new Date(value).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function customerPickerLinkedUid(c = {}) {
  return String(
    c.linkedUserUid ||
    c.linkedUserId ||
    c.userUid ||
    c.userId ||
    c.platformUserUid ||
    c.platformUserId ||
    c.canonicalUserUid ||
    c.customerUid ||
    ""
  ).trim();
}

function customerPickerVerifiedPhone(c = {}) {
  const verified = normalizePhone(
    c.phoneVerifiedE164 ||
    c.customerPhoneVerifiedE164 ||
    ""
  );
  if (!verified) return "";
  if (c.phoneVerified === false || c.phoneIdentityVerified === false) return "";

  const ownerUid = String(c.phoneIdentityOwnerUid || "").trim();
  const linkedUid = customerPickerLinkedUid(c);
  if (ownerUid && linkedUid && ownerUid !== linkedUid) return "";
  return verified;
}

function customerIdentity(c = {}) {
  // Search queries can return the same Firestore document several times.
  // De-dupe that exact document first. Never collapse two distinct CRM docs
  // solely because they share a phone/email; those may be real identity conflicts.
  const documentId = String(
    c.__documentId ||
    c.docId ||
    c.id ||
    c.customerId ||
    c.clientId ||
    ""
  ).trim();
  if (documentId) return `doc:${documentId}`;

  const uid = customerPickerLinkedUid(c);
  if (uid) return `uid:${uid}`;

  const email = normalizeEmail(c.emailNormalized || c.email || "");
  if (email) return `email:${email}`;

  const phone = normalizePhone(c.phoneNormalized || c.phone || "");
  if (phone) return `contact-phone:${phone}`;

  return `name:${normalizeText(c.name || "cliente")}`;
}

function customerPickerCanonicalRow(row = {}, explicitDocumentId = "") {
  const source = row && typeof row === "object" ? row : {};
  const id = String(
    explicitDocumentId ||
    source.__documentId ||
    source.docId ||
    source.id ||
    source.customerId ||
    source.clientId ||
    ""
  ).trim();
  return {
    ...source,
    __documentId: id || source.__documentId || null,
    id: id || null,
    customerId: id || source.customerId || null,
    clientId: id || source.clientId || null,
  };
}


function customerPickerDisplayName(customer = {}, fallback = "") {
  // LOOP WORKER BOOKING CUSTOMER IDENTITY V8B
  // Some input events run while selectedCustomer is intentionally null.
  // Keep the picker safe and return the fallback instead of crashing.
  const source = customer && typeof customer === "object" ? customer : {};
  const firstLast = [source.firstName, source.lastName].filter(Boolean).join(" ");
  const clean = String(
    source.name ||
    source.customerName ||
    source.displayName ||
    source.fullName ||
    source.clientName ||
    firstLast ||
    fallback ||
    ""
  ).replace(/\s+/g, " ").trim();
  return clean;
}

function customerWhatsappOptOut(customer = {}) {
  return customer.whatsappOptOut === true || customer.customerWhatsappOptOut === true || customer.whatsappOptIn === false || customer.customerWhatsappConsent === false;
}

function customerWhatsappLabel(customer = {}) {
  if (customerWhatsappOptOut(customer)) return "No WhatsApp";
  const phone = customer.phoneNormalized || customer.phone || "";
  if (!phone) return "Sin WhatsApp";
  const digits = String(phone).replace(/\D/g, "");
  return digits.length >= 10 ? "WhatsApp listo" : "Teléfono inválido";
}

function formatCustomerPickerPhone(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;

  // Panama display standard across Loop: +507 6150-4816.
  // Do not force clearly non-Panama numbers such as +1 into +507.
  const looksPanama =
    raw.startsWith("+507") ||
    digits.length === 8 ||
    (digits.length === 11 && digits.startsWith("507"));

  if (!looksPanama) return raw;

  try {
    return formatPhoneDisplay(raw, "PA") || raw;
  } catch (_) {
    if (digits.length === 11 && digits.startsWith("507")) {
      const national = digits.slice(3, 11);
      return `+507 ${national.slice(0, 4)}-${national.slice(4)}`;
    }
    if (digits.length === 8) return `+507 ${digits.slice(0, 4)}-${digits.slice(4)}`;
    return raw;
  }
}

function customerIsMergedOrHidden(customer = {}) {
  return customer.hiddenFromCrm === true || customer.archived === true || Boolean(customer.mergedIntoCustomerId || customer.mergedIntoCustomer);
}

function mergeCustomerRows(rows = []) {
  const map = new Map();
  for (const rawRow of rows) {
    const row = customerPickerCanonicalRow(rawRow);
    const key = customerIdentity(row);
    const current = map.get(key);
    if (!current) {
      map.set(key, row);
      continue;
    }
    const curBookings = Number(current.totalBookings || current.importedTotalBookings || 0);
    const rowBookings = Number(row.totalBookings || row.importedTotalBookings || 0);
    map.set(key, {
      ...current,
      ...row,
      id: current.id || row.id,
      name: current.name || row.name,
      phone: current.phone || row.phone,
      phoneNormalized: current.phoneNormalized || row.phoneNormalized,
      email: current.email || row.email,
      emailNormalized: current.emailNormalized || row.emailNormalized,
      totalBookings: Math.max(curBookings, rowBookings),
      importedTotalBookings: Math.max(curBookings, rowBookings),
      importedLastBookingAtText: current.importedLastBookingAtText || row.importedLastBookingAtText || "",
    });
  }
  return Array.from(map.values());
}

async function loadCustomerScanRows(customersCol, maxRows = 3000) {
  const rows = [];
  let lastDoc = null;

  while (rows.length < maxRows) {
    const remaining = maxRows - rows.length;
    const batchSize = Math.min(500, remaining);

    const qBuilt = lastDoc
      ? query(customersCol, orderBy("__name__"), startAfter(lastDoc), limit(batchSize))
      : query(customersCol, orderBy("__name__"), limit(batchSize));

    const snap = await getDocs(qBuilt);
    if (snap.empty) break;

    snap.docs.forEach((d) => {
      rows.push(customerPickerCanonicalRow(d.data() || {}, d.id));
    });

    lastDoc = snap.docs[snap.docs.length - 1];
    if (snap.docs.length < batchSize) break;
  }

  return rows;
}

function getCustomerSearchText(customer = {}) {
  return normalizeText([
    customer.name,
    customer.customerName,
    customer.fullName,
    customer.displayName,
    customer.firstName,
    customer.lastName,
    customer.nameKey,
    customer.searchableName,
    customer.phone,
    customer.phoneNormalized,
    customer.whatsapp,
    customer.whatsApp,
    customer.email,
    customer.emailNormalized,
  ].filter(Boolean).join(" "));
}

function customerMatchesTerm(customer, normalized, digits) {
  if (!normalized) return false;

  const haystack = getCustomerSearchText(customer);
  const queryWords = normalized.split(/\s+/).filter(Boolean);
  const hayWords = haystack.split(/\s+/).filter(Boolean);

  // Exact phrase / direct contains. Example: "del busto" matches "andrea del busto".
  if (haystack.includes(normalized)) return true;

  // For multi-word searches, every meaningful word must exist.
  // This prevents "del busto" from matching random names that only contain "de".
  const meaningfulWords = queryWords.filter((word) => word.length >= 3);
  if (meaningfulWords.length > 1) {
    return meaningfulWords.every((word) =>
      hayWords.some((hw) => hw.includes(word))
    );
  }

  // Single word search. Example: "busto", "flores", "ores".
  if (meaningfulWords.length === 1) {
    const word = meaningfulWords[0];
    return hayWords.some((hw) => hw.includes(word));
  }

  // Numeric phone search.
  if (digits && digits.length >= 3) {
    const phoneText = String(
      customer.phone ||
      customer.phoneNormalized ||
      customer.whatsapp ||
      customer.whatsApp ||
      ""
    ).replace(/\D/g, "");
    if (phoneText.includes(digits)) return true;
  }

  return false;
}


const CUSTOMER_PICKER_SEARCH_CACHE = new Map();
const CUSTOMER_PICKER_CACHE_TTL_MS = 90 * 1000;

function customerPickerCacheKey(businessId, raw) {
  return `${businessId || ""}::${normalizeText(raw || "")}`;
}

function getCachedCustomerPickerResults(key) {
  const row = CUSTOMER_PICKER_SEARCH_CACHE.get(key);
  if (!row) return null;
  if (Date.now() - row.at > CUSTOMER_PICKER_CACHE_TTL_MS) {
    CUSTOMER_PICKER_SEARCH_CACHE.delete(key);
    return null;
  }
  return row.items;
}

function setCachedCustomerPickerResults(key, items) {
  CUSTOMER_PICKER_SEARCH_CACHE.set(key, { at: Date.now(), items: Array.isArray(items) ? items : [] });
  if (CUSTOMER_PICKER_SEARCH_CACHE.size > 80) {
    const first = CUSTOMER_PICKER_SEARCH_CACHE.keys().next().value;
    CUSTOMER_PICKER_SEARCH_CACHE.delete(first);
  }
}

function customerPickerRuntimePlatform() {
  try {
    if (typeof window === "undefined") return "web";
    const cap = window.Capacitor;
    return (cap && (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform)) || "web";
  } catch (_) {
    return "web";
  }
}

function customerPickerIsIosNative() {
  try {
    return isIosCapacitor() || customerPickerRuntimePlatform() === "ios";
  } catch (_) {
    return false;
  }
}

function recordCustomerPickerDebug(event, payload = {}) {
  try {
    if (typeof window === "undefined") return;
    const row = {
      event,
      at: new Date().toISOString(),
      platform: customerPickerRuntimePlatform(),
      ...payload,
    };
    window.__LOOP_CUSTOMER_PICKER_DEBUG__ = row;
    window.__LOOP_CUSTOMER_PICKER_DEBUG_LOG__ = [
      ...(Array.isArray(window.__LOOP_CUSTOMER_PICKER_DEBUG_LOG__) ? window.__LOOP_CUSTOMER_PICKER_DEBUG_LOG__ : []),
      row,
    ].slice(-25);
  } catch (_) {}
}

function customerPickerWithTimeout(promise, timeoutMs = 5500, fallback = []) {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = window.setTimeout(() => resolve(fallback), timeoutMs);
    }),
  ]).finally(() => {
    try { if (timer) window.clearTimeout(timer); } catch (_) {}
  });
}

async function listCustomersNativeSafe(dbClient, businessId, raw, maxRows = 1600) {
  const path = `businesses/${businessId}/customers`;
  const normalized = normalizeText(raw);
  const email = normalizeEmail(raw);
  const phone = normalizePhone(raw);
  const digits = String(raw || "").replace(/\D/g, "");
  const rows = [];
  const seen = new Set();
  const addRows = (items) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (!item) return;
      const id = String(item.id || item.customerId || item.docId || customerIdentity(item) || Math.random());
      const key = `${id}::${customerIdentity(item)}`;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push({ id: item.id || item.customerId || item.docId || id, ...(item || {}) });
    });
  };

  // Exact phone/email lookups are cheap and work through the iOS REST bridge.
  try {
    if (phone && digits.length >= 3) {
      addRows(await customerPickerWithTimeout(fsClient.listCollectionByPath(path, { where: [{ field: "phoneNormalized", op: "EQUAL", value: phone }], limit: 20 }, { db: dbClient }), 4200, []));
    }
  } catch (e) {
    recordCustomerPickerDebug("ios-phone-query-failed", { message: String(e?.message || e) });
  }

  try {
    if (email && String(raw || "").includes("@")) {
      addRows(await customerPickerWithTimeout(fsClient.listCollectionByPath(path, { where: [{ field: "emailNormalized", op: "EQUAL", value: email }], limit: 20 }, { db: dbClient }), 4200, []));
    }
  } catch (e) {
    recordCustomerPickerDebug("ios-email-query-failed", { message: String(e?.message || e) });
  }

  // LOOP R35 EMAIL PREFIX SEARCH V4
  // Indexed partial-email lookup. It also works before the user types "@"
  // when the token has an email-username shape.
  const nativeEmailPrefixRaw = String(raw || "").trim();
  const nativeLooksLikeEmailPrefix = Boolean(
    email &&
    nativeEmailPrefixRaw.length >= 3 &&
    !/\s/.test(nativeEmailPrefixRaw) &&
    (
      nativeEmailPrefixRaw.includes("@") ||
      /^[a-z0-9._%+-]+$/i.test(nativeEmailPrefixRaw)
    )
  );

  try {
    if (nativeLooksLikeEmailPrefix) {
      addRows(await customerPickerWithTimeout(
        fsClient.listCollectionByPath(path, {
          where: [
            { field: "emailNormalized", op: "GREATER_THAN_OR_EQUAL", value: email },
            { field: "emailNormalized", op: "LESS_THAN_OR_EQUAL", value: `${email}\uf8ff` },
          ],
          orderBy: [{ field: "emailNormalized", direction: "ASCENDING" }],
          limit: 40,
        }, { db: dbClient }),
        4200,
        []
      ));
    }
  } catch (e) {
    recordCustomerPickerDebug("ios-email-prefix-query-failed", { message: String(e?.message || e) });
  }

  // iOS/Capacitor cannot rely on Web SDK range queries, but the REST bridge can run
  // prefix queries safely. Do this before the capped scan so names outside the first
  // 700 docs are still discoverable in TestFlight.
  const prefixes = Array.from(new Set([
    normalized,
    ...normalized.split(/\s+/).filter((word) => word.length >= 3),
  ].filter(Boolean))).slice(0, 5);

  for (const prefix of prefixes) {
    for (const field of ["nameKey", "searchableName"]) {
      try {
        const found = await customerPickerWithTimeout(
          fsClient.listCollectionByPath(path, {
            where: [
              { field, op: "GREATER_THAN_OR_EQUAL", value: prefix },
              { field, op: "LESS_THAN_OR_EQUAL", value: `${prefix}\uf8ff` },
            ],
            orderBy: [{ field, direction: "ASCENDING" }],
            limit: prefix === normalized ? 80 : 40,
          }, { db: dbClient }),
          4500,
          []
        );
        addRows(found);
      } catch (e) {
        recordCustomerPickerDebug("ios-name-prefix-query-failed", { field, prefixLength: prefix.length, message: String(e?.message || e) });
      }
    }
  }

  // Final safety net for legacy rows missing nameKey/searchableName. This remains capped,
  // but it is no longer the primary iOS search path.
  try {
    const scanLimit = normalized.length >= 5 || digits.length >= 5 ? maxRows : Math.min(900, maxRows);
    addRows(await customerPickerWithTimeout(fsClient.listCollectionByPath(path, { limit: scanLimit }, { db: dbClient }), 5500, []));
  } catch (e) {
    recordCustomerPickerDebug("ios-customer-scan-failed", { message: String(e?.message || e) });
  }

  recordCustomerPickerDebug("ios-native-safe-search", { businessId, termLength: String(raw || "").length, rows: rows.length });
  return rows;
}


function platformUserDisplayName(data = {}, fallback = "") {
  const first = String(data.firstName || data.nombre || "").trim();
  const last = String(data.lastName || data.apellido || "").trim();
  const firstLast = `${first} ${last}`.trim();
  return [firstLast, data.fullName, data.displayName, data.name, data.email, fallback, "Cliente"]
    .map((x) => String(x || "").replace(/\s+/g, " ").trim())
    .find(Boolean) || "Cliente";
}

function platformUserRowFromDoc(snap, matchedBy = "platform", sourceCollection = "users") {
  if (!snap || !snap.exists?.()) return null;
  const data = snap.data() || {};
  const uid = String(data.uid || data.userUid || data.userId || snap.id || "").trim();
  if (!uid) return null;

  const email = normalizeEmail(
    data.emailNormalized ||
    data.contactEmailNormalized ||
    data.authEmailNormalized ||
    data.email ||
    data.authEmail ||
    data.contactEmail ||
    data.userEmail ||
    ""
  );

  const legacyContactPhone = normalizePhone(
    data.phoneE164 ||
    data.phoneNormalized ||
    data.telefonoNormalized ||
    data.telefono ||
    data.phone ||
    data.phoneDisplay ||
    data.whatsapp ||
    ""
  );
  const verifiedPhone = data.phoneVerified === true
    ? normalizePhone(data.phoneVerifiedE164 || "")
    : "";

  return {
    id: `platform_${uid}`,
    platformUserId: uid,
    platformUserUid: uid,
    linkedUserId: uid,
    linkedUserUid: uid,
    userId: uid,
    userUid: uid,
    name: platformUserDisplayName(data, email || verifiedPhone || "Cliente"),
    email,
    emailNormalized: email,

    // IMPORTANT: platform phone identity is only the SMS/Firebase-verified phone.
    // A legacy profile contact phone remains informational and must never cause a UID merge.
    phone: verifiedPhone,
    phoneNormalized: verifiedPhone,
    phoneE164: verifiedPhone,
    phoneVerified: Boolean(verifiedPhone),
    phoneVerifiedE164: verifiedPhone || null,
    profileContactPhone: legacyContactPhone || null,

    totalBookings: 0,
    importedTotalBookings: 0,
    source: sourceCollection === "pendingRegistrations" ? "pending_registration_match" : "platform_user_match",
    identitySource: sourceCollection === "pendingRegistrations" ? "pending_registration_match" : "platform_user_match",
    identityMatchedBy: matchedBy,
    identitySourceCollection: sourceCollection,
    hasUserAccount: sourceCollection === "users",
    isPlatformUserCandidate: sourceCollection === "users",
    identityCanonicalProfile: sourceCollection === "users",
    identityContactSource: verifiedPhone ? "platform_user_verified_phone" : "platform_user_without_verified_phone",
  };
}

async function searchPlatformUsers(dbClient, rawTerm) {
  const raw = String(rawTerm || "").trim();
  const email = normalizeEmail(raw);
  const phone = normalizePhone(raw);
  const digits = raw.replace(/\D/g, "");
  if (!dbClient || (!raw.includes("@") && digits.length < 6)) return [];

  const out = [];
  const seen = new Set();
  const collectionsToSearch = ["users"];
  const tasks = [];
  const addTask = (collectionName, field, value, matchedBy) => {
    if (!value) return;
    if (customerPickerIsIosNative()) {
      tasks.push(
        customerPickerWithTimeout(fsClient.listCollectionByPath(collectionName, { where: [{ field, op: "EQUAL", value }], limit: 1 }, { db: dbClient }), 4200, [])
          .then((rows) => ({ rows, matchedBy, collectionName }))
          .catch((error) => ({ error, matchedBy, collectionName, field }))
      );
      return;
    }
    tasks.push(
      getDocs(query(collection(dbClient, collectionName), where(field, "==", value), limit(1)))
        .then((snap) => ({ snap, matchedBy, collectionName }))
        .catch((error) => ({ error, matchedBy, collectionName, field }))
    );
  };

  for (const colName of collectionsToSearch) {
    if (raw.includes("@") && email) {
      ["emailNormalized", "contactEmailNormalized", "authEmailNormalized", "email", "authEmail", "contactEmail", "userEmail"].forEach((field) => addTask(colName, field, email, "email"));
    }
    if (phone && digits.length >= 6) {
      // Only a verified phone may resolve a Loop account. Legacy profile phones
      // can still exist as contact data but are not identity keys.
      ["phoneVerifiedE164"].forEach((field) => addTask(colName, field, phone, "verified_phone"));
    }
  }

  const results = await Promise.all(tasks);
  for (const res of results) {
    if (!res || res.error) continue;
    if (Array.isArray(res.rows)) {
      res.rows.forEach((data) => {
        const snapLike = { id: data?.id, exists: () => true, data: () => data || {} };
        const row = platformUserRowFromDoc(snapLike, res.matchedBy, res.collectionName);
        const uid = row?.platformUserUid;
        if (!uid || seen.has(uid)) return;
        seen.add(uid);
        out.push(row);
      });
      continue;
    }
    if (!res.snap || res.snap.empty) continue;
    const row = platformUserRowFromDoc(res.snap.docs[0], res.matchedBy, res.collectionName);
    const uid = row?.platformUserUid;
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    out.push(row);
  }
  return out;
}

function customerPickerPlatformDuplicateIndex(rows = [], platformRow = {}) {
  const uid = String(platformRow.platformUserUid || platformRow.linkedUserUid || "").trim();
  const email = normalizeEmail(platformRow.emailNormalized || platformRow.email || "");
  const verifiedPhone = platformRow.phoneVerified === true
    ? normalizePhone(platformRow.phoneVerifiedE164 || platformRow.phoneNormalized || platformRow.phone || "")
    : "";

  // 1) UID is always strongest.
  if (uid) {
    const uidIndex = rows.findIndex((customer) => customerPickerLinkedUid(customer) === uid);
    if (uidIndex >= 0) return uidIndex;
  }

  // 2) Email can attach the platform profile only if the CRM row is not already
  // linked to a different Loop UID.
  if (email) {
    const emailIndex = rows.findIndex((customer) => {
      const customerUid = customerPickerLinkedUid(customer);
      if (uid && customerUid && customerUid !== uid) return false;
      return normalizeEmail(customer.emailNormalized || customer.email || "") === email;
    });
    if (emailIndex >= 0) return emailIndex;
  }

  // 3) Verified phone is allowed only for an unlinked/offline CRM row or the same UID.
  // A row with another UID/email must remain separate even when the contact phone matches.
  if (verifiedPhone) {
    const phoneIndex = rows.findIndex((customer) => {
      const customerUid = customerPickerLinkedUid(customer);
      if (uid && customerUid && customerUid !== uid) return false;

      const customerEmail = normalizeEmail(customer.emailNormalized || customer.email || "");
      if (email && customerEmail && customerEmail !== email) return false;

      const customerPhone = normalizePhone(customer.phoneNormalized || customer.phone || "");
      return customerPhone === verifiedPhone;
    });
    if (phoneIndex >= 0) return phoneIndex;
  }

  return -1;
}

function customerPickerOverlayPlatformCustomer(businessCustomer = {}, platformRow = {}) {
  const verifiedPhone = platformRow.phoneVerified === true
    ? normalizePhone(platformRow.phoneVerifiedE164 || platformRow.phoneNormalized || platformRow.phone || "")
    : "";

  return {
    ...businessCustomer,
    name: platformRow.name || businessCustomer.name,
    customerName: platformRow.name || businessCustomer.customerName,
    displayName: platformRow.name || businessCustomer.displayName,
    email: platformRow.email || businessCustomer.email,
    emailNormalized: platformRow.emailNormalized || businessCustomer.emailNormalized,

    // Keep the business contact phone when the Loop account itself has no verified phone.
    phone: verifiedPhone || businessCustomer.phone,
    phoneNormalized: verifiedPhone || businessCustomer.phoneNormalized,
    phoneE164: verifiedPhone || businessCustomer.phoneE164,
    customerPhoneNormalized: verifiedPhone || businessCustomer.customerPhoneNormalized,
    whatsapp: verifiedPhone || businessCustomer.whatsapp,
    whatsApp: verifiedPhone || businessCustomer.whatsApp,
    phoneVerified: Boolean(verifiedPhone) || businessCustomer.phoneVerified === true,
    phoneVerifiedE164: verifiedPhone || businessCustomer.phoneVerifiedE164 || null,
    customerPhoneVerifiedE164: verifiedPhone || businessCustomer.customerPhoneVerifiedE164 || null,
    phoneIdentityVerified: Boolean(verifiedPhone) || businessCustomer.phoneIdentityVerified === true,
    phoneIdentityOwnerUid: verifiedPhone
      ? (platformRow.platformUserUid || null)
      : (businessCustomer.phoneIdentityOwnerUid || null),

    hasUserAccount: true,
    linkedUserId: platformRow.platformUserId || businessCustomer.linkedUserId,
    linkedUserUid: platformRow.platformUserUid || businessCustomer.linkedUserUid,
    userId: platformRow.platformUserId || businessCustomer.userId,
    userUid: platformRow.platformUserUid || businessCustomer.userUid,
    platformUserId: platformRow.platformUserId || businessCustomer.platformUserId,
    platformUserUid: platformRow.platformUserUid || businessCustomer.platformUserUid,
    platformUserEmail: platformRow.email || businessCustomer.platformUserEmail,
    platformUserPhone: verifiedPhone || businessCustomer.platformUserPhone || null,
    identitySource: platformRow.identitySource || businessCustomer.identitySource,
    identityMatchedBy: platformRow.identityMatchedBy || businessCustomer.identityMatchedBy,
    identitySourceCollection: platformRow.identitySourceCollection || businessCustomer.identitySourceCollection,
    identityCanonicalProfile: true,
    identityContactSource: verifiedPhone ? "platform_user_verified_phone" : "business_customer_contact",
    isSyncedFromPlatformUser: true,
  };
}

async function searchCustomers(dbClient, businessId, term) {
  const raw = String(term || "").trim();

  if (!dbClient || !businessId || raw.length < 2) {
    return [];
  }

  const cacheKey = customerPickerCacheKey(businessId, raw);
  const cached = getCachedCustomerPickerResults(cacheKey);
  if (cached) return cached;

  const normalized = normalizeText(raw);
  const phone = normalizePhone(raw);
  const email = normalizeEmail(raw);
  const digits = raw.replace(/\D/g, "");

  if (customerPickerIsIosNative()) {
    const nativeRows = await listCustomersNativeSafe(dbClient, businessId, raw);
    const activeRows = nativeRows.filter((customer) => !customerIsMergedOrHidden(customer));
    const merged = mergeCustomerRows(activeRows);
    const filtered = merged.filter((customer) => customerMatchesTerm(customer, normalized, digits));

    filtered.sort((a, b) => {
      const aName = normalizeText(a.name || a.customerName || a.fullName || a.displayName || "");
      const bName = normalizeText(b.name || b.customerName || b.fullName || b.displayName || "");
      const aExact = aName === normalized ? 1 : 0;
      const bExact = bName === normalized ? 1 : 0;
      if (aExact !== bExact) return bExact - aExact;
      const aStarts = aName.startsWith(normalized) ? 1 : 0;
      const bStarts = bName.startsWith(normalized) ? 1 : 0;
      if (aStarts !== bStarts) return bStarts - aStarts;
      const aBookings = Number(a.totalBookings || a.importedTotalBookings || 0);
      const bBookings = Number(b.totalBookings || b.importedTotalBookings || 0);
      return bBookings - aBookings;
    });

    const finalRows = filtered.slice(0, 15);
    try {
      const platformRows = await searchPlatformUsers(dbClient, raw);
      platformRows.forEach((platformRow) => {
        const duplicateIndex = customerPickerPlatformDuplicateIndex(finalRows, platformRow);
        if (duplicateIndex >= 0 && platformRow.identityCanonicalProfile === true) {
          finalRows[duplicateIndex] = customerPickerOverlayPlatformCustomer(
            finalRows[duplicateIndex],
            platformRow
          );
        } else if (duplicateIndex < 0) {
          finalRows.push(platformRow);
        }
      });
    } catch (e) {
      recordCustomerPickerDebug("ios-platform-lookup-failed", { message: String(e?.message || e) });
    }

    setCachedCustomerPickerResults(cacheKey, finalRows);
    recordCustomerPickerDebug("ios-search-finished", { businessId, termLength: raw.length, count: finalRows.length });
    return finalRows;
  }

  const customersCol = collection(
    dbClient,
    `businesses/${businessId}/customers`
  );

  const promises = [];

  // Fast prefix search by full nameKey. No full collection scan on every keypress.
  if (normalized) {
    promises.push(
      getDocs(
        query(
          customersCol,
          orderBy("nameKey"),
          where("nameKey", ">=", normalized),
          where("nameKey", "<=", `${normalized}\uf8ff`),
          limit(18)
        )
      )
    );
  }

  // Prefix search by each meaningful word. Helps with last names.
  const words = normalized.split(/\s+/).filter((w) => w.length >= 3);
  words.slice(0, 4).forEach((word) => {
    promises.push(
      getDocs(
        query(
          customersCol,
          orderBy("nameKey"),
          where("nameKey", ">=", word),
          where("nameKey", "<=", `${word}\uf8ff`),
          limit(18)
        )
      )
    );
  });

  // Last-resort scan if prefix indexes are missing/stale. Several older CRM rows
  // do not have nameKey/searchableName, so mobile would show an empty menu for
  // normal names like "Juan". Keep it capped to avoid freezing large businesses.
  const shouldFallbackScan = normalized.length >= 2 || digits.length >= 3;

  if (phone && digits.length >= 3) {
    promises.push(
      getDocs(
        query(
          customersCol,
          where("phoneNormalized", "==", phone),
          limit(20)
        )
      )
    );
  }

  if (email && raw.includes("@")) {
    promises.push(
      getDocs(
        query(
          customersCol,
          where("emailNormalized", "==", email),
          limit(20)
        )
      )
    );
  }

  // LOOP R35 EMAIL PREFIX SEARCH V4
  // Indexed partial-email lookup:
  //   juanquiros151   -> Gmail + Hotmail
  //   juanquiros151@  -> Gmail + Hotmail
  //   juanquiros151@g -> Gmail
  //   juanquiros151@h -> Hotmail
  const webEmailPrefixRaw = String(raw || "").trim();
  const webLooksLikeEmailPrefix = Boolean(
    email &&
    webEmailPrefixRaw.length >= 3 &&
    !/\s/.test(webEmailPrefixRaw) &&
    (
      webEmailPrefixRaw.includes("@") ||
      /^[a-z0-9._%+-]+$/i.test(webEmailPrefixRaw)
    )
  );

  if (webLooksLikeEmailPrefix) {
    promises.push(
      getDocs(
        query(
          customersCol,
          orderBy("emailNormalized"),
          where("emailNormalized", ">=", email),
          where("emailNormalized", "<=", `${email}\uf8ff`),
          limit(40)
        )
      )
    );
  }

  const snaps = await Promise.allSettled(promises);
  const rows = [];

  snaps.forEach((res) => {
    if (res.status !== "fulfilled") {
      console.warn("[CustomerPicker] customer query failed", res.reason);
      return;
    }

    if (Array.isArray(res.value)) {
      rows.push(...res.value.map((row) => customerPickerCanonicalRow(row)));
      return;
    }

    res.value.docs.forEach((d) => {
      rows.push(customerPickerCanonicalRow(d.data() || {}, d.id));
    });
  });

  if (rows.length === 0 && shouldFallbackScan) {
    try {
      const fallbackRows = await loadCustomerScanRows(customersCol, digits.length >= 3 ? 900 : 700);
      rows.push(...fallbackRows);
    } catch (e) {
      console.warn("[CustomerPicker] fallback customer scan failed", e);
    }
  }

  const activeRows = rows.filter((customer) => !customerIsMergedOrHidden(customer));
  const merged = mergeCustomerRows(activeRows);
  const filtered = merged.filter((customer) => customerMatchesTerm(customer, normalized, digits));

  filtered.sort((a, b) => {
    const aName = normalizeText(a.name || a.customerName || a.fullName || a.displayName || "");
    const bName = normalizeText(b.name || b.customerName || b.fullName || b.displayName || "");

    const aExact = aName === normalized ? 1 : 0;
    const bExact = bName === normalized ? 1 : 0;
    if (aExact !== bExact) return bExact - aExact;

    const aStarts = aName.startsWith(normalized) ? 1 : 0;
    const bStarts = bName.startsWith(normalized) ? 1 : 0;
    if (aStarts !== bStarts) return bStarts - aStarts;

    const queryWords = normalized.split(/\s+/).filter((w) => w.length >= 3);
    const aAllWords = queryWords.every((w) => aName.includes(w)) ? 1 : 0;
    const bAllWords = queryWords.every((w) => bName.includes(w)) ? 1 : 0;
    if (aAllWords !== bAllWords) return bAllWords - aAllWords;

    const aContainsAt = aName.indexOf(normalized);
    const bContainsAt = bName.indexOf(normalized);
    if (aContainsAt !== bContainsAt) {
      if (aContainsAt === -1) return 1;
      if (bContainsAt === -1) return -1;
      return aContainsAt - bContainsAt;
    }

    const aBookings = Number(a.totalBookings || a.importedTotalBookings || 0);
    const bBookings = Number(b.totalBookings || b.importedTotalBookings || 0);
    return bBookings - aBookings;
  });

  const finalRows = filtered.slice(0, 10);

  try {
    const platformRows = await searchPlatformUsers(dbClient, raw);
    platformRows.forEach((platformRow) => {
      const duplicateIndex = customerPickerPlatformDuplicateIndex(finalRows, platformRow);
      if (duplicateIndex >= 0 && platformRow.identityCanonicalProfile === true) {
        finalRows[duplicateIndex] = customerPickerOverlayPlatformCustomer(
          finalRows[duplicateIndex],
          platformRow
        );
      } else if (duplicateIndex < 0) {
        finalRows.push(platformRow);
      }
    });
  } catch (e) {
    console.warn("[CustomerPicker] platform user lookup failed", e);
  }

  setCachedCustomerPickerResults(cacheKey, finalRows);
  recordCustomerPickerDebug("web-search-finished", { businessId, termLength: raw.length, count: finalRows.length, usedFallbackScan: rows.length > snaps.reduce((sum, res) => sum + (res.status === "fulfilled" && res.value?.docs ? res.value.docs.length : 0), 0) });
  return finalRows;
}

function customerPickerIsMobileInputRuntime() {
  try {
    if (typeof window === "undefined") return false;
    const cap = window.Capacitor;
    const platform = cap && (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform);
    if (platform && platform !== "web") return true;
    return Boolean(window.matchMedia && window.matchMedia("(max-width: 980px)").matches);
  } catch (_) {
    return false;
  }
}

function stopCustomerPickerPointerPropagation(event) {
  try { event.stopPropagation(); } catch (_) {}
}

function stabilizeCustomerPickerInput(event) {
  try {
    if (!customerPickerIsMobileInputRuntime()) return;
    const el = event && event.currentTarget ? event.currentTarget : null;
    if (!el || typeof el.scrollIntoView !== "function") return;
    const picker = typeof el.closest === "function" ? el.closest(".loop-customer-picker") : null;
    const target = picker || el;
    try { picker?.classList?.add?.("is-keyboard-focused"); } catch (_) {}
    window.setTimeout(() => {
      try { target.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" }); } catch (_) {}
    }, 80);
    window.setTimeout(() => {
      try { target.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" }); } catch (_) {}
    }, 340);
  } catch (_) {}
}

const EMPTY_CRM_CUSTOMERS = [];

export default function CustomerPicker({
  crmCustomers = EMPTY_CRM_CUSTOMERS,
  businessId,
  value,
  email,
  phone,
  onChange = () => {},
  onUseNewCustomer = () => {},
  disabled = false,
  maxVisibleResults = 3,
  canViewCustomerPhone = true,
  canViewCustomerEmail = true,
}) {
  const [input, setInput] = useState(value || "");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showAllResults, setShowAllResults] = useState(false);
  const [desktopMenuStyle, setDesktopMenuStyle] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const inputFocusedRef = useRef(false);

  useEffect(() => {
    const next = String(value || "");
    // Do not rehydrate the controlled search value while the mobile keyboard is active.
    // Parent state updates on every keystroke; forcing setInput here can reset focus/cursor
    // and make iOS/Android collapse the keyboard.
    if (inputFocusedRef.current && inputRef.current && document.activeElement === inputRef.current) return;
    setInput(next);
    if (!next) setSelectedCustomer(null);
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current?.contains?.(e.target)) return;
      if (menuRef.current?.contains?.(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDoc, true);
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("touchstart", onDoc, true);
    return () => {
      document.removeEventListener("pointerdown", onDoc, true);
      document.removeEventListener("mousedown", onDoc, true);
      document.removeEventListener("touchstart", onDoc, true);
    };
  }, []);

  useEffect(() => {
    if (!open || customerPickerIsMobileInputRuntime()) {
      setDesktopMenuStyle(null);
      return undefined;
    }

    const positionMenu = () => {
      try {
        const anchor = wrapRef.current?.getBoundingClientRect?.();
        if (!anchor) return;

        const viewportWidth = Math.max(320, window.innerWidth || document.documentElement?.clientWidth || 0);
        const viewportHeight = Math.max(320, window.innerHeight || document.documentElement?.clientHeight || 0);
        const gap = 8;
        const edge = 12;
        const desiredWidth = Math.max(anchor.width, 360);
        const width = Math.min(desiredWidth, Math.max(280, viewportWidth - edge * 2));
        const left = Math.min(
          Math.max(edge, anchor.left),
          Math.max(edge, viewportWidth - width - edge)
        );

        const below = Math.max(0, viewportHeight - anchor.bottom - edge - gap);
        const above = Math.max(0, anchor.top - edge - gap);
        const preferAbove = below < 250 && above > below;
        const maxHeight = Math.max(180, Math.min(420, preferAbove ? above : below));
        const top = preferAbove
          ? Math.max(edge, anchor.top - gap - maxHeight)
          : Math.min(viewportHeight - edge - 160, anchor.bottom + gap);

        setDesktopMenuStyle({
          position: "fixed",
          left: `${Math.round(left)}px`,
          top: `${Math.round(top)}px`,
          width: `${Math.round(width)}px`,
          maxHeight: `${Math.round(maxHeight)}px`,
          zIndex: 2147483200,
        });
      } catch (_) {}
    };

    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, input, results.length, showAllResults]);

  useEffect(() => {
    const text = String(input || "").trim();
    if (!businessId || text.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    let alive = true;
    const knownMatches = mergeCustomerRows(crmCustomers.filter((row) => !customerIsMergedOrHidden(row) && customerMatchesTerm(row, normalizeText(text), text.replace(/\D/g, ""))).map((row) => customerPickerCanonicalRow(row)));
    setResults(knownMatches);
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await searchCustomers(db, businessId, text);
        if (alive) {
          setResults(mergeCustomerRows([...knownMatches, ...rows]));
          setShowAllResults(false);
          setActiveIndex(rows.length ? 0 : -1);
        }
      } catch (e) {
        console.warn("[CustomerPicker] search failed", e);
        if (alive) setResults(knownMatches);
      } finally {
        if (alive) setLoading(false);
      }
    }, 280);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [businessId, input, crmCustomers]);

  const selectedMeta = useMemo(() => {
    if (!selectedCustomer) return "";
    const parts = [];
    const selectedPhoneDisplay = canViewCustomerPhone ? formatCustomerPickerPhone(selectedCustomer.phone || selectedCustomer.phoneNormalized || selectedCustomer.phoneE164 || selectedCustomer.customerPhoneNormalized || selectedCustomer.whatsapp || selectedCustomer.whatsApp || "") : "";
    if (selectedPhoneDisplay) parts.push(selectedPhoneDisplay);
    if (canViewCustomerEmail && (selectedCustomer.email || selectedCustomer.emailNormalized)) parts.push(selectedCustomer.email || selectedCustomer.emailNormalized);
    return parts.join(" · ");
  }, [selectedCustomer, canViewCustomerPhone, canViewCustomerEmail]);

  const pick = (customer) => {
    const canonicalCustomer = customerPickerCanonicalRow(customer);
    const typedPhone = normalizePhone(phone || "");
    const typedEmail = normalizeEmail(email || "");
    const useCanonicalProfile = canonicalCustomer.identityCanonicalProfile === true || canonicalCustomer.identityContactSource === "platform_user_profile" || canonicalCustomer.isPlatformUserCandidate === true;
    const pickedName = customerPickerDisplayName(canonicalCustomer, input);
    const selectedPhone = useCanonicalProfile ? (canonicalCustomer.phone || canonicalCustomer.phoneNormalized || "") : (canonicalCustomer.phone || canonicalCustomer.phoneNormalized || typedPhone || "");
    const selectedEmail = useCanonicalProfile ? (canonicalCustomer.email || canonicalCustomer.emailNormalized || "") : (canonicalCustomer.email || canonicalCustomer.emailNormalized || typedEmail || "");
    setSelectedCustomer(customerPickerCanonicalRow({ ...canonicalCustomer, name: pickedName, customerName: pickedName, displayName: pickedName, fullName: pickedName }));
    setInput(pickedName);
    setOpen(false);
    onChange({
      customerId: canonicalCustomer.isPlatformUserCandidate ? null : (canonicalCustomer.id || canonicalCustomer.customerId || null),
      customerName: pickedName,
      customerEmail: selectedEmail,
      customerPhone: selectedPhone,
      customerSnapshot: {
        id: canonicalCustomer.isPlatformUserCandidate ? null : (canonicalCustomer.id || canonicalCustomer.customerId || null),
        customerId: canonicalCustomer.isPlatformUserCandidate ? null : (canonicalCustomer.customerId || canonicalCustomer.id || null),
        name: pickedName,
        customerName: pickedName,
        displayName: pickedName,
        fullName: pickedName,
        phone: selectedPhone,
        phoneNormalized: normalizePhone(selectedPhone),
        phoneE164: normalizePhone(selectedPhone),
        email: selectedEmail,
        emailNormalized: normalizeEmail(selectedEmail),
        hasUserAccount: canonicalCustomer.hasUserAccount === true || canonicalCustomer.isPlatformUserCandidate === true || Boolean(canonicalCustomer.linkedUserId || canonicalCustomer.linkedUserUid || canonicalCustomer.userId || canonicalCustomer.userUid || canonicalCustomer.platformUserId || canonicalCustomer.platformUserUid),
        linkedUserId: canonicalCustomer.linkedUserId || canonicalCustomer.linkedUserUid || canonicalCustomer.userId || canonicalCustomer.userUid || canonicalCustomer.platformUserId || canonicalCustomer.platformUserUid || null,
        linkedUserUid: canonicalCustomer.linkedUserUid || canonicalCustomer.linkedUserId || canonicalCustomer.userUid || canonicalCustomer.userId || canonicalCustomer.platformUserUid || canonicalCustomer.platformUserId || null,
        userId: canonicalCustomer.userId || canonicalCustomer.linkedUserId || canonicalCustomer.platformUserId || null,
        userUid: canonicalCustomer.userUid || canonicalCustomer.linkedUserUid || canonicalCustomer.platformUserUid || null,
        platformUserId: canonicalCustomer.platformUserId || canonicalCustomer.linkedUserId || canonicalCustomer.userId || null,
        platformUserUid: canonicalCustomer.platformUserUid || canonicalCustomer.linkedUserUid || canonicalCustomer.userUid || null,
        platformUserEmail: canonicalCustomer.email || canonicalCustomer.emailNormalized || null,
        platformUserPhone: canonicalCustomer.phone || canonicalCustomer.phoneNormalized || null,
        identitySource: canonicalCustomer.identitySource || (canonicalCustomer.isPlatformUserCandidate ? "platform_user_match" : null),
        identityMatchedBy: canonicalCustomer.identityMatchedBy || null,
        identitySourceCollection: canonicalCustomer.identitySourceCollection || null,
        identityNeedsUserProfileSync: false,
        identityCanonicalProfile: useCanonicalProfile,
        identityContactSource: useCanonicalProfile ? "platform_user_profile" : canonicalCustomer.identityContactSource || null,
        isSyncedFromPlatformUser: canonicalCustomer.isSyncedFromPlatformUser === true || useCanonicalProfile,
        whatsappOptOut: customerWhatsappOptOut(canonicalCustomer),
        whatsappOptIn: !customerWhatsappOptOut(canonicalCustomer),
        customerWhatsappConsent: !customerWhatsappOptOut(canonicalCustomer),
        whatsappPhoneStatus: canonicalCustomer.whatsappPhoneStatus || customerWhatsappLabel({ ...canonicalCustomer, phone: selectedPhone, phoneNormalized: selectedPhone }).toLowerCase().replace(/\s+/g, "_"),
        totalBookings: Number(canonicalCustomer.totalBookings || canonicalCustomer.importedTotalBookings || 0),
        lastBookingAt: canonicalCustomer.lastBookingAt || null,
        importedLastBookingAtText: canonicalCustomer.importedLastBookingAtText || "",
        source: canonicalCustomer.source || "",
      },
    });
  };

  const selectTypedAsNew = () => {
    const name = String(input || "").trim();
    if (!name) return;
    setSelectedCustomer(null);
    setOpen(false);
    onChange({
      customerId: null,
      customerName: name,
      customerEmail: email || "",
      customerPhone: phone || "",
      customerSnapshot: null,
    });
    // Keep focus stable on mobile; blurring here collapses the keyboard and feels broken.
    setTimeout(() => onUseNewCustomer({ customerName: name }), 0);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === "Enter" && (!results.length || activeIndex < 0) && String(input || "").trim().length >= 2) {
      e.preventDefault();
      selectTypedAsNew();
      return;
    }
    if (!results.length) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      pick(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const typedText = String(input || "").trim();
  const safeMaxVisible = Math.max(1, Number(maxVisibleResults || 3));
  const visibleResults = showAllResults ? results : results.slice(0, safeMaxVisible);
  const hiddenResultCount = Math.max(0, results.length - visibleResults.length);

  const menuNode = open && typedText.length >= 2 && !selectedCustomer ? (
    <div
      ref={menuRef}
      className={`lcp-menu lcp-menu-compact ${desktopMenuStyle ? "is-desktop-portal" : ""}`}
      style={desktopMenuStyle || undefined}
      onPointerDown={stopCustomerPickerPointerPropagation}
      onTouchStart={stopCustomerPickerPointerPropagation}
    >
      <button
        type="button"
        className="lcp-use-new"
        onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={selectTypedAsNew}
      >
        <span className="lcp-use-new-plus">+</span>
        <span>
          <strong>Usar “{typedText}” como cliente nuevo</strong>
          <small>{canViewCustomerPhone || canViewCustomerEmail ? "Continúa para agregar email o WhatsApp." : "Continúa para registrar el cliente."}</small>
        </span>
      </button>

      <div className="lcp-menu-head">
        <span>{loading ? "Buscando clientes..." : results.length ? "Clientes encontrados" : "Sin coincidencias"}</span>
        {results.length ? <em>{results.length} resultado{results.length === 1 ? "" : "s"}</em> : null}
      </div>

      <div className="lcp-results-scroll" role="list">
        {visibleResults.map((customer, idx) => {
          const total = Number(customer.totalBookings || customer.importedTotalBookings || 0);
          const last = formatDate(customer.lastBookingAt) || customer.importedLastBookingAtText || "";
          const phoneDisplay = canViewCustomerPhone ? formatCustomerPickerPhone(customer.phone || customer.phoneNormalized || customer.phoneE164 || customer.customerPhoneNormalized || customer.whatsapp || customer.whatsApp || "") : "";
          const emailDisplay = canViewCustomerEmail ? (customer.email || customer.emailNormalized || "") : "";
          const contact = [phoneDisplay, emailDisplay].filter(Boolean).join(" · ");
          return (
            <button
              key={`${customer.id || idx}-${idx}`}
              type="button"
              className={`lcp-row ${idx === activeIndex ? "is-active" : ""}`}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => pick(customer)}
            >
              <span className="lcp-avatar">{initials(customer.name)}</span>
              <span className="lcp-main">
                <strong>{customer.name || "Cliente"}</strong>
                <small>{contact || (canViewCustomerPhone || canViewCustomerEmail ? "Sin teléfono/email visible" : "Contacto privado")}</small>
                {customer.isPlatformUserCandidate ? <small className="lcp-platform-badge">Usuario de Loop</small> : null}
                {canViewCustomerPhone ? <small className={customerWhatsappOptOut(customer) ? "lcp-whatsapp-badge is-off" : "lcp-whatsapp-badge"}>{customerWhatsappLabel(customer)}</small> : null}
              </span>
              <span className="lcp-stats">
                <b>{total}</b>
                <small>{last || "sin última visita"}</small>
              </span>
            </button>
          );
        })}
      </div>

      {hiddenResultCount > 0 ? (
        <button
          type="button"
          className="lcp-show-more"
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowAllResults(true)}
        >
          Ver {hiddenResultCount} resultado{hiddenResultCount === 1 ? "" : "s"} más
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div
      ref={wrapRef}
      className="loop-customer-picker"
      onPointerDown={stopCustomerPickerPointerPropagation}
      onTouchStart={stopCustomerPickerPointerPropagation}
    >
      <style>{styles}</style>
      <label className="lcp-label">Cliente</label>
      <div
        className={`lcp-input-shell ${open ? "is-open" : ""} ${selectedCustomer ? "is-selected" : ""}`}
        onPointerDown={stopCustomerPickerPointerPropagation}
        onTouchStart={stopCustomerPickerPointerPropagation}
      >
        <span className="lcp-icon">⌕</span>
        <input
          ref={inputRef}
          value={input}
          disabled={disabled}
          onPointerDown={stopCustomerPickerPointerPropagation}
          onTouchStart={stopCustomerPickerPointerPropagation}
          onFocus={(e) => { inputFocusedRef.current = true; setOpen(true); stabilizeCustomerPickerInput(e); }}
          onBlur={() => {
            window.setTimeout(() => {
              try {
                inputFocusedRef.current = document.activeElement === inputRef.current;
                if (!inputFocusedRef.current) wrapRef.current?.classList?.remove?.("is-keyboard-focused");
              } catch (_) { inputFocusedRef.current = false; }
            }, 140);
          }}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            const next = e.target.value;
            const selectedName = customerPickerDisplayName(selectedCustomer, "");
            const sameAsSelected = Boolean(selectedCustomer && selectedName && normalizeText(next) === normalizeText(selectedName));
            setInput(next);
            if (sameAsSelected) {
              setOpen(false);
              setShowAllResults(false);
              return;
            }
            setSelectedCustomer(null);
            setOpen(true);
            setShowAllResults(false);
            onChange({
              customerId: null,
              customerName: next,
              customerEmail: email || "",
              customerPhone: phone || "",
              customerSnapshot: null,
            });
          }}
          placeholder={canViewCustomerPhone || canViewCustomerEmail ? "Busca por nombre, teléfono o email" : "Busca por nombre"}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="words"
          spellCheck={false}
          enterKeyHint="done"
        />
        {loading ? <span className="lcp-spinner" /> : selectedCustomer ? <span className="lcp-check">✓</span> : null}
      </div>

      {selectedMeta ? <div className="lcp-selected-note">Cliente seleccionado · {selectedMeta}</div> : null}

      {menuNode
        ? (desktopMenuStyle && typeof document !== "undefined"
          ? createPortal(menuNode, document.body)
          : menuNode)
        : null}

    </div>
  );
}

const styles = `
.loop-customer-picker{position:relative;min-width:0}
.lcp-label{display:block;font-size:12px;font-weight:850;color:#0f172a;margin-bottom:6px}
.lcp-input-shell{height:44px;border:1px solid #dbe3ee;border-radius:14px;background:#fff;display:flex;align-items:center;gap:8px;padding:0 11px;transition:160ms ease;box-shadow:0 1px 0 rgba(15,23,42,.02)}
.lcp-input-shell.is-open{border-color:#0f766e;box-shadow:0 0 0 4px rgba(20,184,166,.10)}
.lcp-input-shell.is-selected{border-color:#14b8a6;background:#f8fffd}
.lcp-icon{font-size:18px;color:#64748b;line-height:1}
.lcp-input-shell input{border:0;outline:0;background:transparent;width:100%;height:100%;font-size:14px;font-weight:750;color:#0f172a;min-width:0}
.lcp-input-shell input::placeholder{color:#94a3b8;font-weight:650}
.lcp-check{height:22px;width:22px;border-radius:999px;background:#dcfce7;color:#047857;display:grid;place-items:center;font-weight:950;font-size:13px}
.lcp-spinner{width:17px;height:17px;border-radius:999px;border:2px solid #e2e8f0;border-top-color:#0f766e;animation:lcpSpin .7s linear infinite}
.lcp-selected-note{font-size:11px;color:#0f766e;font-weight:800;margin-top:5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lcp-menu{position:absolute;z-index:2147481000;top:calc(100% + 8px);left:0;right:0;background:#fff;border:1px solid #e2e8f0;border-radius:18px;box-shadow:0 18px 48px rgba(15,23,42,.14);overflow:hidden;max-height:236px;display:flex;flex-direction:column;overscroll-behavior:contain;scrollbar-width:thin}
.lcp-menu-compact{max-height:236px}
.lcp-menu-head{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 12px;border-top:1px solid #f1f5f9;border-bottom:1px solid #f1f5f9;background:#f8fafc;font-size:10px;font-weight:950;color:#64748b;text-transform:uppercase;letter-spacing:.06em;flex:0 0 auto}
.lcp-menu-head em{font-style:normal;color:#0f766e}
.lcp-use-new{width:100%;border:0;background:#ffffff;display:grid;grid-template-columns:30px minmax(0,1fr);gap:10px;align-items:center;text-align:left;padding:11px 12px;cursor:pointer;flex:0 0 auto}
.lcp-use-new:hover{background:#f0fdfa}
.lcp-use-new-plus{width:28px;height:28px;border-radius:999px;background:#0f766e;color:#fff;display:grid;place-items:center;font-weight:950;font-size:16px}
.lcp-use-new strong{display:block;font-size:12px;font-weight:950;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lcp-use-new small{display:block;margin-top:2px;font-size:11px;font-weight:700;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lcp-row{width:100%;border:0;background:#fff;display:grid;grid-template-columns:34px minmax(0,1fr) auto;align-items:center;gap:9px;text-align:left;padding:9px 12px;cursor:pointer;border-bottom:1px solid #f8fafc;flex:0 0 auto}
.lcp-row:hover,.lcp-row.is-active{background:#f0fdfa}
.lcp-avatar{width:32px;height:32px;border-radius:999px;background:#ecfeff;color:#0f766e;font-size:11px;font-weight:950;display:grid;place-items:center;border:1px solid #ccfbf1}
.lcp-main{min-width:0;display:grid;gap:1px}.lcp-main strong{font-size:13px;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.lcp-main small{font-size:11px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lcp-stats{text-align:right;display:grid;gap:1px;color:#475569}.lcp-stats b{font-size:12px;color:#0f172a}.lcp-stats small{font-size:10px;color:#94a3b8;white-space:nowrap}
.lcp-show-more{width:100%;border:0;background:#f8fafc;color:#0f766e;padding:9px 12px;font-size:11px;font-weight:950;cursor:pointer;flex:0 0 auto;border-top:1px solid #f1f5f9}
.lcp-show-more:hover{background:#ecfdf5}
.lcp-platform-badge{display:inline-flex!important;align-items:center!important;width:max-content!important;max-width:100%!important;margin-top:2px!important;padding:2px 7px!important;border-radius:999px!important;background:#eefaf7!important;border:1px solid #ccfbf1!important;color:#00675f!important;font-size:10px!important;font-weight:950!important;line-height:1.25!important}
@keyframes lcpSpin{to{transform:rotate(360deg)}}
@media(max-width:640px){.lcp-menu{position:absolute;left:0;right:0;top:calc(100% + 8px);bottom:auto;max-height:228px;overflow:auto;border-radius:18px}.lcp-row{grid-template-columns:32px minmax(0,1fr);}.lcp-stats{grid-column:2;text-align:left;display:flex;gap:8px}.lcp-selected-note{white-space:normal}.lcp-menu-head{position:sticky;top:0;z-index:1}.lcp-use-new{grid-template-columns:28px minmax(0,1fr)}}


/* LOOP MOBILE KEYBOARD STABILITY V1 */
.loop-customer-picker,
.lcp-input-shell,
.lcp-menu {
  touch-action: manipulation;
}
.lcp-input-shell input {
  -webkit-user-select: text !important;
  user-select: text !important;
}
@media (max-width: 980px) {
  .lcp-menu {
    max-height: min(48dvh, 360px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}
/* LOOP CUSTOMER PICKER V73 — mobile dropdown visibility */
.loop-create-pro-modal .loop-customer-picker {
  z-index: 2147481200 !important;
}
.loop-create-pro-modal .lcp-menu {
  z-index: 2147483000 !important;
}
@media (max-width: 760px) {
  .loop-create-pro-modal .loop-customer-picker,
  .loop-create-pro-modal .lcp-input-shell {
    overflow: visible !important;
  }
  .loop-create-pro-modal .lcp-menu {
    min-height: 152px !important;
    max-height: min(44dvh, 340px) !important;
  }
}



/* LOOP CUSTOMER PICKER MOBILE LAYERS V94 — compact mobile results + sane dropdown z-index */
.loop-create-pro-modal .loop-customer-picker{
  z-index:auto!important;
}
.loop-create-pro-modal .lcp-menu{
  z-index:170!important;
}
@media(max-width:760px){
  .lcp-menu{
    max-height:min(38dvh,260px)!important;
    border-radius:15px!important;
    box-shadow:0 14px 36px rgba(15,23,42,.16)!important;
  }
  .lcp-use-new{
    grid-template-columns:24px minmax(0,1fr)!important;
    gap:8px!important;
    min-height:42px!important;
    padding:8px 10px!important;
  }
  .lcp-use-new-plus{
    width:24px!important;
    height:24px!important;
    font-size:14px!important;
  }
  .lcp-use-new strong{
    font-size:11.5px!important;
    line-height:1.1!important;
  }
  .lcp-use-new small{
    display:none!important;
  }
  .lcp-menu-head{
    position:static!important;
    min-height:24px!important;
    padding:5px 10px!important;
    font-size:8.5px!important;
    line-height:1!important;
    letter-spacing:.055em!important;
  }
  .lcp-row{
    grid-template-columns:28px minmax(0,1fr)!important;
    gap:8px!important;
    min-height:46px!important;
    padding:7px 10px!important;
  }
  .lcp-avatar{
    width:28px!important;
    height:28px!important;
    font-size:10px!important;
  }
  .lcp-main strong{
    font-size:12px!important;
    line-height:1.12!important;
  }
  .lcp-main small{
    font-size:10px!important;
    line-height:1.14!important;
  }
  .lcp-whatsapp-badge,
  .lcp-platform-badge,
  .lcp-stats{
    display:none!important;
  }
  .lcp-show-more{
    min-height:34px!important;
    padding:7px 10px!important;
    font-size:10.5px!important;
  }
}
@media(max-width:380px){
  .lcp-menu{
    max-height:min(36dvh,235px)!important;
  }
}


/* LOOP CUSTOMER PICKER MOBILE SHEET GUARD V95
   Separate the scrollable results from the "Ver más" action so the CTA never floats on top of a row. */
.lcp-menu{
  display:flex!important;
  flex-direction:column!important;
  overflow:hidden!important;
}
.lcp-results-scroll{
  flex:1 1 auto!important;
  min-height:0!important;
  overflow-y:auto!important;
  overflow-x:hidden!important;
  -webkit-overflow-scrolling:touch!important;
  overscroll-behavior:contain!important;
}
.lcp-show-more{
  flex:0 0 auto!important;
  position:relative!important;
  bottom:auto!important;
  z-index:2!important;
  box-shadow:0 -1px 0 rgba(226,232,240,.9)!important;
}
@media(max-width:760px){
  .lcp-menu{
    max-height:min(46dvh,340px)!important;
  }
  .lcp-results-scroll{
    max-height:none!important;
    padding-bottom:2px!important;
  }
  .lcp-show-more{
    min-height:42px!important;
    margin:0!important;
    border-radius:0 0 13px 13px!important;
    background:#ffffff!important;
    border-top:1px solid #e2e8f0!important;
  }
}
@media(max-width:380px){
  .lcp-menu{
    max-height:min(44dvh,310px)!important;
  }
}




/* LOOP BOOKING UX V2 A2.6 — desktop customer picker portal.
   Desktop results escape modal clipping; the results body owns scrolling. */
.lcp-menu.is-desktop-portal{
  right:auto!important;
  bottom:auto!important;
  margin:0!important;
  overflow:hidden!important;
  display:flex!important;
  flex-direction:column!important;
  border-radius:16px!important;
  box-shadow:0 24px 70px rgba(15,23,42,.20)!important;
}
.lcp-menu.is-desktop-portal .lcp-results-scroll{
  flex:1 1 auto!important;
  min-height:0!important;
  max-height:none!important;
  overflow-y:auto!important;
  overflow-x:hidden!important;
}
.lcp-menu.is-desktop-portal .lcp-use-new,
.lcp-menu.is-desktop-portal .lcp-menu-head,
.lcp-menu.is-desktop-portal .lcp-show-more{
  flex:0 0 auto!important;
}

/* LOOP CUSTOMER PICKER MOBILE KEYBOARD V13 */
@media(max-width:760px){
  .loop-customer-picker.is-keyboard-focused .lcp-menu{
    position:fixed!important;
    left:14px!important;
    right:14px!important;
    top:auto!important;
    bottom:calc(18px + env(safe-area-inset-bottom,0px))!important;
    max-height:min(42dvh,330px)!important;
    z-index:2147483200!important;
    border-radius:20px!important;
    box-shadow:0 24px 80px rgba(15,23,42,.32)!important;
  }
  .loop-customer-picker.is-keyboard-focused .lcp-use-new{
    min-height:46px!important;
  }
  .loop-customer-picker.is-keyboard-focused .lcp-results-scroll{
    max-height:none!important;
  }
}

`;
